// React-related imports
import { FC, useMemo } from 'react';

// Components
import SvgIcon from '../elements/SvgIcon';
import { Button, Dropdown, Search } from '@GxT-Team/gxt-components';

// Types
import { ButtonConfigSelect, TableActionBarProps } from './TableActionBar.types';

// Helpers
import { getDropdownOptions } from '../../helpers/utils';

// Extracted so useMemo can stabilize initialValue — passing an inline object causes an infinite
// update loop inside Dropdown's useEffect([initialValue]).
const SelectDropdownButton: FC<{ button: ButtonConfigSelect }> = ({ button }) => {
  const initialValue = useMemo(
    () => ({ value: button.defaultValue }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [button.defaultValue?.title, button.defaultValue?.value],
  );
  const defaultCustomStyles = useMemo(
    () => ({
      container: { height: '100%' },
      button: { whiteSpace: 'nowrap' as const, height: '100%' },
      itemsWrapper: { zIndex: 100, width: 'fit-content' },
    }),
    [],
  );
  return (
    <Dropdown
      options={getDropdownOptions(button.options)}
      type={button.type}
      dropdownIcon
      disabled={button.disabled}
      initialValue={initialValue}
      renderOption={(item) => item.value.title}
      position="bottom"
      onSelect={(option) => button.onSelect && button.onSelect(option.value)}
      customStyles={button.customStyles ?? defaultCustomStyles}
      equalityFn={(a, b) => a?.value === b?.value}
      placeholder={button.placeholder}
    />
  );
};

const TableActionBar: FC<TableActionBarProps> = ({ handleSearch, buttons, searchTerm = '' }) => {
  return (
    <div className="flex w-full gap-2">
      {handleSearch && (
        <Search
          value={searchTerm}
          onChangeCallback={handleSearch}
          width="100%"
          className="h-full"
        />
      )}
      {buttons.map((button) => (
        <div key={button.id}>
          {button.type === 'select' ? (
            <SelectDropdownButton button={button} />
          ) : (
            <Button
              variant={button.variant}
              onClick={button.onClick}
              disabled={button.disabled}
              id={button.id}
              data-testid={button.dataTestId}
              className={`!h-full ${button.styles}`}>
              {button.title}
              {button.iconType && (
                <SvgIcon
                  data-testid={button.iconType || 'svg'}
                  iconType={button.iconType}
                  stroke={button.disabled ? 'var(--neutral-200, #9CA3AF)' : undefined}
                />
              )}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export default TableActionBar;
